import * as React from "react";
import { ClientStatus, IClient } from "intake_shared/src/types";
import { TSaveClient } from "../../../../clientUiTypes";
import { AclContext } from "../../../../../../../contexts/AclContext";
import { useToasts } from "react-toast-notifications";
import { CacheContext } from "../../../../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../../../../utils/intakeEmailSignature";
import store from "../../../../../../../store";
import { sendAssignClientEmail } from "../../../../../../../utils/queue_email";
import { Button } from "../../../../../bits/Button/Button";
import PlusCircleIcon from "../../../../../icons/PlusCircle";
import { Select } from "../../../../../bits/Select/Select";
import { TextArea } from "../../../../../bits/TextArea/TextArea";
import styles from "./AssignClient.module.scss";
import { cn } from "../../../../../../helpers";
import { Dialog } from "../../../../../bits/Dialog/Dialog";

interface AssignClientProps {
    client: IClient;
    onSaveClient: TSaveClient;
    setActiveKey: React.Dispatch<React.SetStateAction<string>>;
    open?: boolean;
    onClose?: () => void;
    hideTriggerButton?: boolean;
    onAssigned?: (updated: IClient) => void;
}

const REMOVE_SECONDARY_ASSIGNEMENT_LABEL = "Remove Secondary Assignment";
const REMOVE_PRIMARY_ASSIGNMENT_LABEL = "Remove Primary Assignment";

type Option = {
    label: React.ReactNode;
    value: string;
};

export default function AssignClient({
    client,
    onSaveClient,
    setActiveKey,
    open,
    onClose,
    hideTriggerButton,
    onAssigned,
}: AssignClientProps) {
    const { addToast } = useToasts();
    const aclContext = React.useContext(AclContext);
    const [primaryOfficer, setPrimaryOfficer] = React.useState(
        client.assigned_to as string
    );
    const olc = React.useContext(CacheContext);
    const intakeSignature = getIntakeEmailSignature(olc);
    const [primaryOfficerTouched, setPrimaryOfficerTouched] = React.useState(false);
    const [secondaryOfficerTouched, setSecondaryOfficerTouched] =
        React.useState(false);
    const [secondaryOfficer, setSecondaryOfficer] = React.useState(
        client.secondary_assignee as string
    );
    const [assignmentNotes, setAssignmentNotes] = React.useState("");
    const [showAssignClient, setShowAssignClient] = React.useState(false);
    const acls = React.useContext(CacheContext)?.allUserRoles;
    const allAcls = acls?.map((acl) => acl.email);

    React.useEffect(() => {
        if (typeof open === "boolean") {
            setShowAssignClient(open);
        }
    }, [open]);

    React.useEffect(() => {
        setPrimaryOfficer(client.assigned_to as string);
        setSecondaryOfficer(client.secondary_assignee as string);
    }, [client]);

    const onConfirmAssignment = async (officer?: string) => {
        const last_updated_by: string = aclContext?.userAcl.email!;
        try {
            if (officer === REMOVE_PRIMARY_ASSIGNMENT_LABEL) {
                if (client.status === ClientStatus.COMPLETED) {
                    await store.updateArchivedClient(client.id, {
                        ...client,
                        assigned_to: "",
                    });
                } else {
                    await store.updateClientData({
                        client_id: client.id,
                        changes: { assigned_to: "" },
                        last_updated_by,
                    });
                }
                addToast(`Client unassigned`, {
                    appearance: "success",
                });
                onSaveClient({
                    ...client,
                    assigned_to: "",
                });
                onAssigned?.({ ...client, assigned_to: "" });
                setPrimaryOfficer("");
                setSecondaryOfficer("");
                if (onClose) onClose();
                setShowAssignClient(false);
                return;
            }
            if (officer === REMOVE_SECONDARY_ASSIGNEMENT_LABEL) {
                if (client.status === ClientStatus.COMPLETED) {
                    await store.updateArchivedClient(client.id, {
                        ...client,
                        secondary_assignee: "",
                    });
                } else {
                    await store.updateClientData({
                        client_id: client.id,
                        changes: { secondary_assignee: "" },
                        last_updated_by,
                    });
                }
                addToast(`Client unassigned`, {
                    appearance: "success",
                });
                onSaveClient({
                    ...client,
                    secondary_assignee: "",
                });
                onAssigned?.({ ...client, secondary_assignee: "" });
                setPrimaryOfficer("");
                setSecondaryOfficer("");
                if (onClose) onClose();
                setShowAssignClient(false);
                return;
            }
            const updatedClient = {
                ...client,
                assigned_to: primaryOfficer,
                secondary_assignee: secondaryOfficer,
            };

            if (client.status === ClientStatus.COMPLETED) {
                await store.updateArchivedClient(client.id, {
                    ...client,
                    assigned_to: updatedClient.assigned_to,
                    secondary_assignee: updatedClient.secondary_assignee,
                });
            } else {
                await store.updateClientData({
                    client_id: client.id,
                    changes: {
                        assigned_to: updatedClient.assigned_to,
                        secondary_assignee: updatedClient.secondary_assignee,
                    },
                    last_updated_by,
                });
            }

            if (primaryOfficerTouched) {
                await sendAssignClientEmail(
                    updatedClient,
                    primaryOfficer,
                    assignmentNotes,
                    intakeSignature
                );
            }
            if (secondaryOfficerTouched) {
                await sendAssignClientEmail(
                    updatedClient,
                    secondaryOfficer,
                    assignmentNotes,
                    intakeSignature
                );
            }

            addToast(`Client assigned successfully`, {
                appearance: "success",
            });
            onSaveClient({
                ...client,
                assigned_to: primaryOfficer,
                secondary_assignee: secondaryOfficer,
            });
            onAssigned?.(updatedClient);
            setPrimaryOfficer("");
            setSecondaryOfficer("");
            if (onClose) onClose();
            setShowAssignClient(false);
        } catch (ex) {
            console.log(ex);
            addToast(`Error occured, please contact your administrator`, {
                appearance: "error",
            });
        }
    };

    const { intakeOfficers = [], intakeOfficerColors = {} } =
        aclContext?.intakeConfig || {};

    const getPrimaryAssigneeOptions = () => {
        return [...intakeOfficers]
            .sort((a, b) => a.localeCompare(b))
            .map((officer) => ({
                value: officer,
                label: (
                    <div
                        className={styles.assignees}
                        style={{
                            borderLeft: `10px solid ${
                                intakeOfficerColors[officer] || "#fff"
                            }`,
                        }}
                    >
                        {officer}
                    </div>
                ),
            }));
    };

    const getSecondaryAssigneeOptions = () => {
        return allAcls
            ?.sort((a, b) => a.localeCompare(b))
            .map((officer) => ({
                value: officer,
                label: (
                    <div
                        className={styles.assignees}
                        style={{
                            borderLeft: `10px solid ${
                                intakeOfficerColors[officer] || "#fff"
                            }`,
                        }}
                    >
                        {officer}
                    </div>
                ),
            }));
    };

    return (
        <>
            {!hideTriggerButton && (
                <Button
                    mode="outline"
                    onClick={() => setShowAssignClient(true)}
                    iconLeft={<PlusCircleIcon />}
                >
                    Assign client
                </Button>
            )}
            <Dialog
                open={showAssignClient}
                onClose={() => {
                    if (onClose) onClose();
                    setShowAssignClient(false);
                }}
                onSave={async () => {
                    await onConfirmAssignment();
                }}
                title="Assign client"
                saveText="Assign"
            >
                <div className="v2-form">
                    <div className={styles.gridContainer}>
                        <div
                            className={cn(
                                styles.primarySelect,
                                client.assigned_to && styles.hasAssignee
                            )}
                        >
                            <Select
                                onChange={(val) => {
                                    setPrimaryOfficer((val as Option).value);
                                    setPrimaryOfficerTouched(true);
                                }}
                                options={[
                                    { label: "Select option", value: "" },
                                    ...getPrimaryAssigneeOptions(),
                                ]}
                                value={getPrimaryAssigneeOptions().find(
                                    (assignee) => assignee.value === primaryOfficer
                                )}
                                placeholder="Primary assignee"
                                isLongListInDialog
                            />
                        </div>
                        {client.assigned_to && (
                            <div className={styles.removeButton}>
                                <Button
                                    onClick={async () => {
                                        await onConfirmAssignment(
                                            REMOVE_PRIMARY_ASSIGNMENT_LABEL
                                        );
                                    }}
                                    variant="normal"
                                    className={styles.removeButton}
                                >
                                    Remove
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className={styles.gridContainer}>
                        <div
                            className={cn(
                                styles.secondarySelect,
                                client.secondary_assignee && styles.hasAssignee
                            )}
                        >
                            <Select
                                onChange={(val) => {
                                    setSecondaryOfficer((val as Option).value);
                                    setSecondaryOfficerTouched(true);
                                }}
                                options={[
                                    {
                                        label: "Select option",
                                        value: "",
                                    },
                                    ...(getSecondaryAssigneeOptions() || []),
                                ]}
                                value={getSecondaryAssigneeOptions()?.find(
                                    (assignee) => assignee.value === secondaryOfficer
                                )}
                                placeholder="Secondary assignee"
                                isLongListInDialog
                            />
                        </div>
                        {client.secondary_assignee && (
                            <div className={styles.removeButton}>
                                <Button
                                    onClick={async () => {
                                        await onConfirmAssignment(
                                            REMOVE_SECONDARY_ASSIGNEMENT_LABEL
                                        );
                                    }}
                                    variant="normal"
                                    className={styles.removeButton}
                                >
                                    Remove
                                </Button>
                            </div>
                        )}
                    </div>
                    <TextArea
                        label="Notes"
                        value={assignmentNotes}
                        onChange={(e) => {
                            setAssignmentNotes(e.target.value);
                        }}
                    />
                </div>
            </Dialog>
        </>
    );
}
